import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Icons live in `public/icons/<name>.svg` (replace the files there to restyle them). They are drawn
 * as a CSS mask, so they take the text colour of the button (theme, hover, disabled).
 */
export function Icon({ name, size = 16 }: { name: string; size?: number }) {
  const url = `url(${import.meta.env.BASE_URL}icons/${name}.svg)`;
  return <span className="ico" aria-hidden="true" style={{ width: size, height: size, WebkitMaskImage: url, maskImage: url }} />;
}

export type MenuItem =
  | { kind?: 'action'; label: string; icon?: string; shortcut?: string; disabled?: boolean; title?: string; onSelect(): void }
  | { kind: 'check'; label: string; icon?: string; shortcut?: string; checked: boolean; disabled?: boolean; title?: string; onToggle(): void }
  | { kind: 'sub'; label: string; icon?: string; items: MenuItem[] }
  | { kind: 'sep' };

function MenuList({ items, at, onClose, depth }: { items: MenuItem[]; at: { x: number; y: number }; onClose(): void; depth: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(at);
  const [open, setOpen] = useState<{ index: number; at: { x: number; y: number } } | null>(null);
  // Keep the list on screen.
  useLayoutEffect(() => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ x: Math.max(4, Math.min(at.x, window.innerWidth - r.width - 4)), y: Math.max(4, Math.min(at.y, window.innerHeight - r.height - 4)) });
  }, [at.x, at.y]);
  return (
    <div ref={ref} className="menu-list" role="menu" style={{ left: pos.x, top: pos.y, zIndex: 50 + depth }}>
      {items.map((it, i) => {
        if (it.kind === 'sep') return <div key={i} className="menu-sep" role="separator" />;
        const sub = it.kind === 'sub';
        const disabled = !sub && !!it.disabled;
        const openSub = (el: HTMLElement) => {
          const r = el.getBoundingClientRect();
          setOpen({ index: i, at: { x: r.right - 2, y: r.top - 4 } });
        };
        return (
          <button
            key={i}
            className={`menu-item ${sub && open?.index === i ? 'on' : ''}`}
            role={it.kind === 'check' ? 'menuitemcheckbox' : 'menuitem'}
            aria-checked={it.kind === 'check' ? it.checked : undefined}
            aria-haspopup={sub || undefined}
            disabled={disabled}
            title={!sub ? it.title : undefined}
            onMouseEnter={(e) => (sub ? openSub(e.currentTarget) : setOpen(null))}
            onClick={(e) => {
              if (sub) return openSub(e.currentTarget);
              if (it.kind === 'check') it.onToggle();
              else it.onSelect();
              onClose();
            }}
          >
            <span className="menu-mark">{it.kind === 'check' ? <input type="checkbox" tabIndex={-1} readOnly checked={it.checked} /> : it.icon ? <Icon name={it.icon} /> : null}</span>
            <span className="menu-label">{it.label}</span>
            <span className="menu-key">{sub ? '▸' : 'shortcut' in it && it.shortcut ? it.shortcut : ''}</span>
          </button>
        );
      })}
      {open && (items[open.index] as { kind: 'sub'; items: MenuItem[] }).items && (
        <MenuList key={open.index} items={(items[open.index] as { items: MenuItem[] }).items} at={open.at} onClose={onClose} depth={depth + 1} />
      )}
    </div>
  );
}

/** Menu bar button ("Dodaj", "Prikaz") with a drop-down list, sub-menus and checkboxes. */
export function MenuButton({ label, items, icon }: { label: ReactNode; items: MenuItem[]; icon?: string }) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const root = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!at) return;
    const down = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest('.menu-list') && !root.current?.contains(t)) setAt(null);
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && (e.stopPropagation(), setAt(null));
    window.addEventListener('pointerdown', down, true);
    window.addEventListener('keydown', key, true);
    return () => {
      window.removeEventListener('pointerdown', down, true);
      window.removeEventListener('keydown', key, true);
    };
  }, [at]);
  return (
    <span ref={root} className="menu-root">
      <button
        className={`btn sm menu-btn ${at ? 'on' : ''}`}
        aria-haspopup="menu"
        aria-expanded={!!at}
        onClick={(e) => {
          if (at) return setAt(null);
          const r = e.currentTarget.getBoundingClientRect();
          setAt({ x: r.left, y: r.bottom + 2 });
        }}
      >
        {icon && <Icon name={icon} />}
        {label}
        <Icon name="dropdown" size={12} />
      </button>
      {at && <MenuList items={items} at={at} onClose={() => setAt(null)} depth={0} />}
    </span>
  );
}
